/** @typedef {import('quasar').QNotifyCreateOptions} QNotifyCreateOptions */
/** @typedef {import('quasar').QNotifyUpdateOptions} QNotifyUpdateOptions */
import { Dialog } from 'quasar';

import {
  notifyInfo,
  notifyNegative,
  notifySuccess,
  notifyWarning,
  openComponentDialog,
} from '../design-system';

export default {
  methods: {
    /**
     * @deprecated Import `notifySuccess` directly from the design system
     * (`import { notifySuccess } from 'src/design-system'`); this mixin method is a pass-through.
     * @param {string} message
     * @param {QNotifyCreateOptions} opts
     * @return {(props?: QNotifyUpdateOptions) => void | undefined}
     */
    notifySuccess(message, opts = {}) {
      return notifySuccess(message, opts);
    },

    /**
     * @deprecated Import `notifyInfo` directly from the design system
     * (`import { notifyInfo } from 'src/design-system'`); this mixin method is a pass-through.
     * @param {string} message
     * @param {QNotifyCreateOptions} opts
     * @return {(props?: QNotifyUpdateOptions) => void | undefined}
     */
    notifyInfo(message, opts = {}) {
      return notifyInfo(message, opts);
    },

    /**
     * @deprecated Import `notifyNegative` directly from the design system
     * (`import { notifyNegative } from 'src/design-system'`); this mixin method is a pass-through.
     * For an error toast with a copyable-details action, use `notifyErrorWithDetails` from
     * `src/utils/AppUtils` instead.
     * @param {string} message
     * @param {QNotifyCreateOptions} opts
     * @return {(props?: QNotifyUpdateOptions) => void | undefined}
     */
    notifyNegative(message, opts = {}) {
      return notifyNegative(message, opts);
    },

    /**
     * @deprecated Import `notifyWarning` directly from the design system
     * (`import { notifyWarning } from 'src/design-system'`); this mixin method is a pass-through.
     * @param {string} message
     * @param {QNotifyCreateOptions} opts
     * @return {(props?: QNotifyUpdateOptions) => void | undefined}
     */
    notifyWarning(message, opts = {}) {
      return notifyWarning(message, opts);
    },

    /**
     * @deprecated Use `confirmDialog` from the design system (`import { useDialog } from 'src/design-system'`)
     * instead of this mixin method. This legacy version passes options straight to Quasar's `Dialog.create`
     * (so `html`, `options`, `prompt`, `ok`/`cancel` still work); the M3 replacement renders themed dialogs
     * and requires migrating those to `promptDialog`/`radioDialog`/`checkboxDialog`/`confirmDestructiveDialog`.
     * Kept only as the migration bridge until the last caller moves off it.
     */
    confirmDialog(title, message, onOk, opts = {}, onCancel = () => {}) {
      if (!onOk) {
        return;
      }

      return Dialog.create({
        title,
        message,
        cancel: true,
        persistent: true,
        ...opts,
      })
        .onOk(onOk)
        .onCancel(onCancel);
    },

    /**
     * @deprecated Use `alertDialog` from the design system (`import { useDialog } from 'src/design-system'`)
     * instead of this mixin method. This legacy version passes options straight to Quasar's `Dialog.create`
     * (so `html` still works); the M3 replacement renders plain supporting text (use `\n` for line breaks, not
     * HTML). Links/lists/rich content belong in an `openComponentDialog`, not the message. Kept only as the
     * migration bridge until callers move off it.
     */
    alertDialog(title, message, opts) {
      return Dialog.create({
        title,
        message,
        ...opts,
      }).onOk(() => {});
    },

    /**
     * @deprecated Import `openComponentDialog` directly from the design system
     * (`import { openComponentDialog } from 'src/design-system'`); this mixin method is a pass-through.
     */
    openComponentDialog(component, props, onOk, onCancel, onDismiss, opts = {}) {
      return openComponentDialog(component, props, onOk, onCancel, onDismiss, opts);
    },
  },
};
